/**
 * Topic risk drawer laid out as a sentence: the resulting topic risk, based on the combined
 * inherent risk, adjusted by the assessment result.
 */
import DateDisplay from '@app/src/components/DateDisplay'
import { AssessmentResultCard } from '@app/src/pages/TopicalRisk/Drawer/AssessmentResultCard'
import {
  hasEffectiveAssessmentResult,
  mayBeCappedAtInherentRisk,
} from '@app/src/pages/TopicalRisk/Drawer/assessmentResultLogic'
import { ConnectorLabel } from '@app/src/pages/TopicalRisk/Drawer/ConnectorLabel'
import { InherentRiskCard } from '@app/src/pages/TopicalRisk/Drawer/InherentRiskCard'
import { TopicRiskSummaryCard } from '@app/src/pages/TopicalRisk/Drawer/TopicRiskSummaryCard'
import { useEffectiveAssessment } from '@app/src/pages/TopicalRisk/Drawer/useEffectiveAssessment'
import type { SustainabilityTopicRisk } from '@app/src/types/topicalRisk'
import { Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

export interface TopicalRiskDrawerBodyProps {
  sustainabilityTopicRisk: SustainabilityTopicRisk
}

export const TopicalRiskDrawerBody: React.FC<TopicalRiskDrawerBodyProps> = ({ sustainabilityTopicRisk }) => {
  const { formatMessage } = useIntl()
  const { effectiveAssessment } = useEffectiveAssessment(sustainabilityTopicRisk)

  return (
    <Stack spacing={2} p={2}>
      <TopicRiskSummaryCard
        riskStatus={sustainabilityTopicRisk.totalRiskStatus}
        hasAssessmentResult={hasEffectiveAssessmentResult(sustainabilityTopicRisk)}
        mayBeCappedAtInherentRisk={mayBeCappedAtInherentRisk(sustainabilityTopicRisk, effectiveAssessment)}
      />
      <ConnectorLabel label={formatMessage({ id: 'topicalRisk.drawer.basedOn' })} />
      <InherentRiskCard sustainabilityTopicRisk={sustainabilityTopicRisk} />
      <ConnectorLabel label={formatMessage({ id: 'topicalRisk.drawer.adjustedBy' })} />
      <AssessmentResultCard sustainabilityTopicRisk={sustainabilityTopicRisk} />
      <Typography variant="body2" color="text.secondary" align="center" pt={2}>
        {formatMessage({ id: 'general.lastUpdated' })} <DateDisplay value={sustainabilityTopicRisk.createdAt} />
      </Typography>
    </Stack>
  )
}

export default TopicalRiskDrawerBody
